import { Checkbox } from '@/shared/ui/forms/inputs/checkbox'
import { Select } from '@/shared/ui/forms/inputs/select'
import { VLayout } from '@/shared/ui/layouts/flex'

import { type ChangeEvent, type FC, useCallback, useMemo } from 'react'

import { usePermissionPanel } from './usePermissionPanel'

/**
 * PermissionPanel - Panel content for toggling permissions in the testing panel.
 *
 * Displays two blocks:
 * - **User Permissions**: all permissions from the initial user snapshot. Toggling
 *   a permission adds/removes it from `userAtom.permissions[]`.
 * - **Page Permissions**: permissions declared by the current page via
 *   `usePagePermissions`. Toggling writes to the same atom, so overlapping
 *   permissions between blocks stay in sync automatically.
 *
 * Meant to be embedded inside the TestingPanel — has no floating wrapper.
 *
 * @example
 * ```tsx
 * <PermissionPanel />
 * ```
 */
export const PermissionPanel: FC = () => {
  const {
    userPermissionEntries,
    pagePermissionEntries,
    handleTogglePermission,
    handleSetUserPermissions,
    hasPagePermissions,
  } = usePermissionPanel()

  const makeChangeHandler = useCallback(
    (permission: string) =>
      (e: ChangeEvent<HTMLInputElement>): void => {
        handleTogglePermission(permission, e.target.checked)
      },
    [handleTogglePermission],
  )

  const userPermissionOptions = useMemo(
    () =>
      userPermissionEntries.map(entry => ({ value: entry.permission, label: entry.permission })),
    [userPermissionEntries],
  )

  const activeUserPermissions = useMemo(
    () => userPermissionEntries.filter(entry => entry.active).map(entry => entry.permission),
    [userPermissionEntries],
  )

  const handleUserPermissionsChange = useCallback(
    (values: string[]): void => {
      handleSetUserPermissions(values)
    },
    [handleSetUserPermissions],
  )

  return (
    <VLayout gap="2">
      {/* Page Permissions block */}
      <div>
        <p className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Page Permissions
        </p>
        {hasPagePermissions ? (
          <div className="flex flex-col gap-2">
            {pagePermissionEntries.map(entry => (
              <div key={entry.key} className="flex flex-col gap-0.5">
                <Checkbox.Option
                  checked={entry.active}
                  onChange={makeChangeHandler(entry.permission)}
                  size="md"
                >
                  <VLayout gap="2">
                    <span className="font-mono text-sm">{entry.key}</span>
                    <span className="text-xs text-gray-400">
                      <span className="font-mono">{entry.permission}</span>
                      {entry.description ? ` — ${entry.description}` : null}
                    </span>
                  </VLayout>
                </Checkbox.Option>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-3 text-center text-sm text-gray-400">
            No permissions declared on this page.
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-3">
        <p className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
          User Permissions
        </p>
        {userPermissionEntries.length === 0 ? (
          <div className="py-3 text-center text-sm text-gray-400">
            No permissions loaded yet. Open this panel after login.
          </div>
        ) : (
          <Select
            mode="multiple"
            options={userPermissionOptions}
            value={activeUserPermissions}
            onChange={handleUserPermissionsChange}
            placeholder="Select permissions..."
          >
            <Select.LocalSearch />
          </Select>
        )}
      </div>
    </VLayout>
  )
}
